/**
 * API client centralizado para o modulo de chamados.
 *
 * Tipos compartilhados + funcoes de API tipadas para evitar
 * duplicacao de interfaces e endpoints inline nos componentes.
 */

import { api } from './client'

// ─── Tipos Compartilhados ────────────────────────────────────────────────────

export type Priority = 'low' | 'medium' | 'high' | 'critical'
export type RequestType = 'approval' | 'incident' | 'service_request' | 'change_request' | 'gmud'

export type EnvName = 'dev' | 'qa' | 'hml' | 'prd'

export interface EnvironmentChainStep {
  environment: EnvName
  tenant_id: number
}

export interface EnvironmentChainResponse {
  chain: EnvironmentChainStep[]
  current_environment: string | null
}

export interface ChamadoListItem {
  id: number
  title: string
  description?: string
  status: string
  request_type?: RequestType
  priority?: Priority
  protocol_number?: string
  category_name?: string
  assignee_name?: string
  created_at?: string
  updated_at?: string
  requested_by_name?: string
}

export interface ChamadoListResponse {
  items: ChamadoListItem[]
  total: number
}

export interface ChamadoDetail {
  id: number
  title: string
  description: string
  status: string
  request_type: string
  priority: string
  protocol_number: string | null
  requested_by_name: string
  assignee: { id: number; full_name: string } | null
  category: {
    id: number
    name: string
    sla_first_response_hours: number | null
    sla_resolution_hours: number | null
  } | null
  resource_type: string | null
  resource_id: string | null
  resource_name: string | null
  scheduled_start: string | null
  scheduled_end: string | null
  resolved_at: string | null
  closed_at: string | null
  /** Prazo da validação do solicitante (96h, TASK-2789). */
  validation_due_at?: string | null
  created_at: string
  updated_at: string
  decision_reason: string | null
  custom_fields: Record<string, unknown> | null
  events: Array<{
    id: number
    actor_name: string
    event_type: string
    from_status: string | null
    to_status: string | null
    note: string | null
    created_at: string
  }>
}

export interface Category {
  id: number
  tenant_id: number | null
  name: string
  description: string
  default_priority: string
  default_request_type: string
  sla_first_response_hours: number | null
  sla_resolution_hours: number | null
  sla_pause_on_waiting: boolean
  auto_close_after_days: number
  is_active: boolean
  pre_approved?: boolean
  custom_fields_schema?: Array<{
    name: string
    label: string
    type: 'text' | 'number' | 'select' | 'multiselect' | 'textarea' | 'date'
    required?: boolean
    options?: string[]
  }>
}

export interface CategoryPayload {
  name: string
  description: string
  default_priority: string
  default_request_type: string
  sla_first_response_hours?: number
  sla_resolution_hours?: number
  sla_pause_on_waiting: boolean
  auto_close_after_days: number
  is_active: boolean
  pre_approved?: boolean
}

// ─── Relatório de Mudanças (evidência PCI) ──────────────────────────────────

export interface ChangeReportFilters {
  environment?: string
  date_from?: string
  date_to?: string
  category_id?: number
  status?: string
}

export interface ChangeReportRow {
  protocol_number: string
  request_type: string
  category: string | null
  current_environment: string | null
  status: string
  requester: number | null
  approver: number | null
  executor: number | null
  validator: number | null
  segregation_ok: boolean
  signed_off: number | null
  has_rollback: boolean
  has_evidence: boolean
}

export interface FormTemplate {
  id: number
  tenant_id: number
  name: string
  description: string
  category_id: number | null
  category_name?: string
  default_priority: string
  default_request_type: string
  title_prefix: string
  description_template: string
  custom_fields_defaults: Record<string, unknown>
  is_active: boolean
}

export interface FormTemplatePayload {
  name: string
  description: string
  category_id?: number
  default_priority: string
  default_request_type: string
  title_prefix: string
  description_template: string
  custom_fields_defaults?: Record<string, unknown>
  is_active: boolean
}

export interface AlertRule {
  id: number
  tenant_id: number
  alert_source: string
  condition: string
  category_id: number | null
  default_priority: string
  auto_assignee_user_id: number | null
  cooldown_minutes: number
  is_active: boolean
}

export interface AlertRulePayload {
  alert_source: string
  condition: string
  category_id?: number
  default_priority: string
  auto_assignee_user_id?: number
  cooldown_minutes: number
  is_active: boolean
}

// ─── Inbox (caixa de entrada cross-tenant) ──────────────────────────────────

export type InboxReason =
  | 'team' | 'team_unassigned' | 'assignee' | 'waiting_response'
  // GMUD esperando ação deste usuário: iniciar revisão técnica, votar no CCM
  // ou executar a janela agendada.
  | 'gmud_action'
  // Chamado esperando a validação deste usuário: atendimento finalizado,
  // validação técnica ou aceite da execução.
  | 'aguardando_validacao'
  // Aberto e sem responsável numa fila de que o usuário é membro (TASK-12181).
  | 'minhas_filas'

export interface InboxItem {
  id: number
  title: string
  status: string
  priority: string
  tenant_id: number
  tenant_name: string | null
  target_template_id: number | null
  target_template_name: string | null
  /** Fila que atende (só vem na aba `minhas_filas`). */
  queue_name?: string | null
  requested_by_user_id: number | null
  requester_email: string | null
  requester_name: string | null
  assignee_user_id: number | null
  created_at: string
  inbox_reason: InboxReason
  // Agrupamento de sub-tarefas na Entrada: pai (null = chamado avulso).
  parent_task_id: number | null
  parent_title: string | null
}

/** GET /v1/me/inbox/counts — contagem por pasta, sem página (1 query no servidor). */
export interface InboxCounts {
  assigned?: number
  team?: number
  waiting?: number
  sent?: number
  resolved?: number
  archived?: number
  gmud_action?: number
  aguardando_validacao?: number
  /** Dos `assigned`, só os em triagem/em atendimento/reaberto. */
  assigned_actionable?: number
  // Entrada Mailbox (spec 2026-10-19 §4.1): as pastas novas, do MESMO predicado da lista.
  meus_chamados?: number
  minhas_filas?: number
  team_unassigned?: number
  team_assigned?: number
  parados?: number
  /** Quantos chamados de cada pasta estão em negrito para quem olha. */
  negrito_por_pasta?: Partial<Record<PastaDaEntrada, number>>
  /** Feriado de Manaus que é dia útil nacional, para quem não é de Manaus (§3). */
  aviso_feriado_local?: { local: string; data: string } | null
}

/** As pastas da Entrada, com a chave que o backend usa na lista e na contagem
 *  (`chamados.entrada.PASTAS`). */
export type PastaDaEntrada =
  | 'assigned' | 'gmud_action' | 'aguardando_validacao' | 'waiting' | 'meus_chamados'
  | 'minhas_filas' | 'team_unassigned' | 'team_assigned' | 'parados'

/** As colunas pelas quais a Entrada ordena (`chamados.entrada.ORDENS`, spec 2026-10-21). */
export type OrdemDaEntrada = 'id' | 'solicitante' | 'responsavel' | 'prioridade' | 'espera'
export type DirecaoDaOrdem = 'asc' | 'desc'

/** O último movimento humano visível a quem olha (`chamados.entrada.Movimento`). */
export interface MovimentoDaEntrada {
  tipo: string
  autor_user_id: number
  /** "Você" quando o autor é quem olha. */
  autor_primeiro_nome: string
  instante: string
  /** Texto puro, até 160 caracteres, cortado no servidor. */
  trecho: string
  do_solicitante: boolean
  /** `to_status` quando o movimento foi mudança de status. */
  para_status: string | null
}

export interface EntradaItem extends Omit<InboxItem, 'inbox_reason'> {
  inbox_reason: PastaDaEntrada
  request_type?: string | null
  protocol_number?: string | null
  organizacao_id: number | null
  organizacao_nome: string | null
  ultimo_movimento: MovimentoDaEntrada
  negrito: boolean
  /** Quem olha é o responsável. */
  estrela: boolean
  espera_dias_uteis: number
  /** De quem é a vez: 'atendimento' (o solicitante falou por último) ou 'solicitante'. */
  vez: 'atendimento' | 'solicitante'
  triagem_iniciada_por_nome?: string | null
  /** A triagem foi concluída nesta chegada (spec 2026-10-21 §4.6). */
  triagem_concluida?: boolean
  /** Nome da fila do chamado (spec 2026-10-21 §5); `null` sem fila (GMUD). */
  fila_nome?: string | null
  /** Quem está com o chamado (spec 2026-10-22); `null` sem responsável. */
  assignee_name?: string | null
  assignee_email?: string | null
}

/** GET /v1/me/inbox?pasta= — a lista de uma pasta, ordenada no servidor (§5.6). */
export interface EntradaResponse {
  items: EntradaItem[]
  total: number
  limit: number
  offset: number
  pasta: PastaDaEntrada
  /** Organizações com chamados na pasta (antes do filtro `org`). */
  organizacoes: { id: number; nome: string | null; total: number }[]
  /** Filas de quem olha com chamados na pasta (antes do filtro `fila`); só nas pastas de fila. */
  filas: { id: number; nome: string | null; total: number }[]
  /** De quantas filas quem olha é membro: decide se a caixa de Filas aparece. */
  minhas_filas_total: number
}

// Chave do menu e a invalidação única moram em `chamadosPendencias.ts`.
export { CHAVE_PENDENCIAS_DO_MENU, invalidarPendenciasDoChamado } from './chamadosPendencias'

export interface InboxResponse {
  items: InboxItem[]
  groups: {
    team?: number[]
    assignee?: number[]
    waiting_response?: number[]
    aguardando_validacao?: number[]
    minhas_filas?: number[]
  }
  total: number
  limit: number
  offset: number
}

// ─── Sub-tarefas (filhas de um chamado-pai) ─────────────────────────────────

export interface SubTaskItem {
  id: number
  title: string
  status: string
  priority: string | null
  protocol_number: string | null
  created_at: string
  assignee_user_id: number | null
  target_template_id: number | null
  team_name: string | null
  assignee_name: string | null
}

// ─── API Functions ───────────────────────────────────────────────────────────

const base = (tenantId: number) => `/v1/tenants/${tenantId}`

export const chamadosApi = {
  // Chamados (workflow)
  list: (tenantId: number, params?: Record<string, unknown>) =>
    api.get<ChamadoListResponse>(`${base(tenantId)}/workflow`, { params }),

  get: (tenantId: number, id: number) =>
    api.get<ChamadoDetail>(`${base(tenantId)}/workflow/${id}`),

  /** Abre (ou reabre) a conversa de atendimento do chamado com o @Atendente. */
  iniciarAtendimentoIa: (id: number) =>
    api.post<{ conversation_id: number; criada: boolean; tem_resumo: boolean }>(
      `/v1/chamados/${id}/atendimento-ia`,
    ),

  /** Spec 2026-10-16 §4.3: encerra o atendimento com IA e cancela os pedidos que não tocaram o repo. */
  encerrarAtendimentoIa: (id: number) =>
    api.post<{
      conversation_id: number
      pedidos_cancelados: number[]
      ja_encerrada: boolean
      atribuicao: 'feita' | 'nao_precisou' | 'recusada'
      atribuicao_motivo?: string
    }>(`/v1/chamados/${id}/atendimento-ia/encerrar`),

  subTasks: (tenantId: number, parentId: number) =>
    api.get<SubTaskItem[]>(`${base(tenantId)}/workflow/${parentId}/sub-tasks`),

  create: (tenantId: number, data: Record<string, unknown>) =>
    api.post(`${base(tenantId)}/workflow`, data),

  assign: (tenantId: number, id: number, assigneeUserId: number | null) =>
    api.patch(`${base(tenantId)}/workflow/${id}/assign`, { assignee_user_id: assigneeUserId }),

  transition: (tenantId: number, id: number, toStatus: string, note?: string) =>
    api.patch(`${base(tenantId)}/workflow/${id}/status`, { to_status: toStatus, note }),

  // Workflow de promoção por ambiente (sub-projeto A)
  approveRequest: (tenantId: number, id: number, note?: string) =>
    api.post(`${base(tenantId)}/workflow/${id}/approve-request`, { note }),

  executeChange: (tenantId: number, id: number, note?: string) =>
    api.post(`${base(tenantId)}/workflow/${id}/execute`, { note }),

  validateExecution: (tenantId: number, id: number, note?: string) =>
    api.post(`${base(tenantId)}/workflow/${id}/validate-execution`, { note }),

  signoffExecution: (tenantId: number, id: number, note?: string) =>
    api.post(`${base(tenantId)}/workflow/${id}/signoff`, { note }),

  promoteChange: (tenantId: number, id: number, note?: string) =>
    api.post(`${base(tenantId)}/workflow/${id}/promote`, { note }),

  getChain: (tenantId: number, id: number) =>
    api.get<EnvironmentChainResponse>(`${base(tenantId)}/workflow/${id}/chain`),

  // Categories
  listCategories: (tenantId: number) =>
    api.get<Category[]>(`${base(tenantId)}/ticket-categories`),

  createCategory: (tenantId: number, data: CategoryPayload) =>
    api.post(`${base(tenantId)}/ticket-categories`, data),

  updateCategory: (tenantId: number, id: number, data: Partial<CategoryPayload>) =>
    api.put(`${base(tenantId)}/ticket-categories/${id}`, data),

  // Relatório de Mudanças (evidência PCI)
  getChangeReport: (tenantId: number, filters: ChangeReportFilters = {}) =>
    api.get<ChangeReportRow[]>(`${base(tenantId)}/change-reports`, { params: filters }),

  downloadChangeReportCsv: (tenantId: number, filters: ChangeReportFilters = {}) =>
    api.get(`${base(tenantId)}/change-reports`, {
      params: { ...filters, format: 'csv' },
      responseType: 'blob',
    }),

  // Form Templates
  listTemplates: (tenantId: number) =>
    api.get<FormTemplate[]>(`${base(tenantId)}/form-templates`),

  getTemplate: (tenantId: number, id: number) =>
    api.get<FormTemplate>(`${base(tenantId)}/form-templates/${id}`),

  createTemplate: (tenantId: number, data: FormTemplatePayload) =>
    api.post(`${base(tenantId)}/form-templates`, data),

  updateTemplate: (tenantId: number, id: number, data: Partial<FormTemplatePayload>) =>
    api.put(`${base(tenantId)}/form-templates/${id}`, data),

  deleteTemplate: (tenantId: number, id: number) =>
    api.delete(`${base(tenantId)}/form-templates/${id}`),

  // Alert Rules
  listAlertRules: (tenantId: number) =>
    api.get<AlertRule[]>(`${base(tenantId)}/alert-rules`),

  createAlertRule: (tenantId: number, data: AlertRulePayload) =>
    api.post(`${base(tenantId)}/alert-rules`, data),

  updateAlertRule: (tenantId: number, id: number, data: Partial<AlertRulePayload>) =>
    api.put(`${base(tenantId)}/alert-rules/${id}`, data),

  deleteAlertRule: (tenantId: number, id: number) =>
    api.delete(`${base(tenantId)}/alert-rules/${id}`),

  // Attachments
  presign: (tenantId: number, taskId: number, data: { filename: string; mime_type: string; size_bytes: number }) =>
    api.post(`${base(tenantId)}/workflow/${taskId}/attachments/presign`, data),

  confirmUpload: (tenantId: number, taskId: number, data: Record<string, unknown>) =>
    api.post(`${base(tenantId)}/workflow/${taskId}/attachments`, data),

  listAttachments: (tenantId: number, taskId: number) =>
    api.get(`${base(tenantId)}/workflow/${taskId}/attachments`),

  downloadAttachment: (tenantId: number, taskId: number, attachmentId: number) =>
    api.get<{ url: string }>(`${base(tenantId)}/workflow/${taskId}/attachments/${attachmentId}`),

  deleteAttachment: (tenantId: number, taskId: number, attachmentId: number) =>
    api.delete(`${base(tenantId)}/workflow/${taskId}/attachments/${attachmentId}`),

  // Comments
  listComments: (tenantId: number, taskId: number) =>
    api.get(`${base(tenantId)}/workflow/${taskId}/comments`),

  addComment: (tenantId: number, taskId: number, data: { content: string; is_internal?: boolean }) =>
    api.post(`${base(tenantId)}/workflow/${taskId}/comments`, data),

  // Sub-tasks
  listSubTasks: (tenantId: number, taskId: number) =>
    api.get(`${base(tenantId)}/workflow/${taskId}/sub-tasks`),

  createSubTask: (tenantId: number, taskId: number, data: Record<string, unknown>) =>
    api.post(`${base(tenantId)}/workflow/${taskId}/sub-tasks`, data),

  // Metrics
  metrics: (tenantId: number) =>
    api.get(`${base(tenantId)}/chamados/metrics`),

  volume: (tenantId: number) =>
    api.get(`${base(tenantId)}/chamados/volume`),

  teamLoad: (tenantId: number) =>
    api.get(`${base(tenantId)}/chamados/team-load`),

  /**
   * GET /v1/me/inbox — Caixa de Entrada agregadora.
   * Combina chamados de 3 fontes: time (target_template_id=meu template),
   * atribuidos (assignee_user_id=eu) e aguardando resposta (status=
   * waiting_customer + requester=eu). Items tem `inbox_reason` pra
   * agrupar no frontend.
   */
  getInbox: (opts: { limit?: number; offset?: number; aba?: 'minhas_filas' } = {}) => {
    const params = new URLSearchParams()
    if (opts.limit != null) params.set('limit', String(opts.limit))
    if (opts.offset != null) params.set('offset', String(opts.offset))
    if (opts.aba) params.set('aba', opts.aba)
    const qs = params.toString() ? `?${params.toString()}` : ''
    return api.get<InboxResponse>(`/v1/me/inbox${qs}`)
  },

  /** GET /v1/me/inbox?pasta= — Entrada Mailbox: só a pasta, com busca e filtros de organização e fila. */
  getPasta: (opts: {
    pasta: PastaDaEntrada; q?: string; org?: number | null; fila?: number | null
    ordem?: OrdemDaEntrada | null; direcao?: DirecaoDaOrdem | null; limit?: number; offset?: number
  }) => {
    const params: Record<string, string> = { pasta: opts.pasta }
    if (opts.ordem) params.ordem = opts.ordem
    if (opts.ordem && opts.direcao) params.direcao = opts.direcao
    if (opts.q) params.q = opts.q
    if (opts.org != null) params.org = String(opts.org)
    if (opts.fila != null) params.fila = String(opts.fila)
    if (opts.limit != null) params.limit = String(opts.limit)
    if (opts.offset != null) params.offset = String(opts.offset)
    return api.get<EntradaResponse>('/v1/me/inbox', { params })
  },

  /** POST /v1/chamados/{id}:mark-unread — recua a leitura; 404 para quem não lê o chamado. */
  marcarNaoLido: (id: number) =>
    api.post<{ status: 'unread' | 'sem_movimento'; task_request_id: number }>(`/v1/chamados/${id}:mark-unread`),

  /** GET /v1/me/inbox/counts — contagens por pasta (inclui `aguardando_validacao`). */
  getInboxCounts: () => api.get<InboxCounts>('/v1/me/inbox/counts'),

  /**
   * PATCH /v1/chamados/{id} — encaminha chamado pra outro time.
   * Rota non-tenant-scoped: backend resolve tenant via task_request_id.
   * `motivo` (opcional, <=500 chars) e persistido no audit event.
   */
  routeChamado: (id: number, target_template_id: number, motivo?: string) => {
    return api.patch<void>(`/v1/chamados/${id}`, { target_template_id, motivo })
  },

  /** GET /v1/chamados/{id}/tramitar/destinos — para onde o usuário pode tramitar (spec 2026-09-30). */
  getTramitarDestinos: (id: number) => api.get<TramitarOpcoes>(`/v1/chamados/${id}/tramitar/destinos`),

  /** POST /v1/chamados/{id}/tramitar — muda o chamado de tenant; motivo com >=10 caracteres. */
  tramitar: (id: number, body: { tenant_destino_id: number; motivo: string; category_id?: number | null }) =>
    api.post<TramitarResultado>(`/v1/chamados/${id}/tramitar`, body),
}

export interface TramitarDestino {
  tenant_id: number
  tenant_nome: string
  org_id: number | null
  org_nome: string | null
  precisa_categoria: boolean
  categorias: { id: number; nome: string }[]
}
export interface TramitarOpcoes { bloqueio: string | null; destinos: TramitarDestino[] }
export interface TramitarResultado { task_request_id: number; de_tenant: number; para_tenant: number }

// ─── Como funciona o chamado (spec 2026-10-26-chamados-como-funciona §4) ─────

export type AtorDoCaminho = 'pessoa' | 'sistema' | 'admin'
export type AbaDoFluxo = 'atendimento' | 'mudanca'

/** `de` = '*' vale para qualquer estado aberto; `para` = 'anterior' volta ao estado de antes. */
export interface CaminhoDoFluxo {
  de: string | string[]
  para: string
  ator: AtorDoCaminho
  condicao: string
  funcao: string
  publico: 'todos' | 'atendimento'
  aba: AbaDoFluxo
}

export interface EntradaDoFluxo { estado: string; funcao: string; condicao: string; aba: AbaDoFluxo }

export interface MapaDoFluxo {
  estados: string[]
  caminhos: CaminhoDoFluxo[]
  entradas: EntradaDoFluxo[]
  encerrados: string[]
  pausam: string[]
  abas: Record<AbaDoFluxo, string[]>
  regras_sla: {
    limiar_de_risco: number
    primeira_resposta_conta_em: string[]
    horario_util: string
    prazo_validacao_horas: number
    prazo_fechar_resolvido_dias: number
    [regra: string]: unknown
  }
}

export interface SlaDeAgora { no_prazo: number; em_risco: number; vencidos: number; pausados: number; sem_prazo: number }

export interface NumerosDoFluxo {
  escopo: 'own' | 'org' | 'global'
  por_estado: Record<string, number>
  abertos_total: number
  sla: SlaDeAgora
  /** `null` no escopo own: Relatórios não tem o cumprimento só do solicitante. */
  cumprimento: { total_with_sla: number; within_sla: number; compliance_pct: number | null } | null
  /** Contado por par (de, para): setas com o mesmo par mostram o mesmo número. */
  uso_90d: { de: string; para: string; n: number }[] | null
}

export const fluxoApi = {
  mapa: () => api.get<MapaDoFluxo>('/v1/chamados/fluxo/mapa'),
  agora: (params?: { scope?: 'own' | 'org' | 'global'; organizationId?: number }) =>
    api.get<NumerosDoFluxo>('/v1/chamados/fluxo/agora', {
      params: { scope: params?.scope, organization_id: params?.organizationId },
    }),
}
